import React from "react";
import { Check, Infinity as InfinityIcon, ShieldCheck, Smartphone, Sparkles, X, Zap } from "lucide-react";
import { Reveal } from "./Reveal";
import { toast } from "sonner";

const BENEFITS = [
  { icon: Zap, text: "99,99 zł/mies. za nielimitowany dostęp — taniej niż konkurencja żąda BEZ umowy rocznej" },
  { icon: ShieldCheck, text: "Zero zobowiązań — anuluj kiedy chcesz, jednym kliknięciem" },
  { icon: Sparkles, text: "Ebooki i audiobooki w jednym miejscu" },
  { icon: Smartphone, text: "Nowoczesna, wygodna aplikacja — czytaj i słuchaj tam, gdzie Ci wygodnie" },
];

const PLANS = [
  { key: "start", name: "Start", price: "39,99", desc: "Idealny na początek przygody", limit: "3 tytuły / mies." },
  { key: "czytelnik", name: "Czytelnik", price: "59,99", desc: "Dla regularnie czytających", limit: "8 tytułów / mies." },
  { key: "bez-limitu", name: "Bez Limitu", price: "99,99", desc: "Pełna biblioteka bez ograniczeń", limit: "Bez limitu tytułów", featured: true },
];

const ROWS = [
  ["Limit tytułów / miesiąc", "3", "8", "Bez limitu"],
  ["Ebooki", 1, 1, 1],
  ["Audiobooki", 0, 1, 1],
  ["Dostęp offline (pobrane pliki)", 1, 1, 1],
  ["Liczba urządzeń jednocześnie", "1", "2", "4"],
  ["Wczesny dostęp do nowości (48h przed premierą)", 0, 0, 1],
  ["Synchronizacja postępu między urządzeniami", 0, 1, 1],
  ["Personalizowane rekomendacje (quiz / AI)", 1, 1, 1],
  ["Zniżka na dodatkowe tytuły poza limitem", "—", "10%", "20%"],
  ["Priorytetowe wsparcie klienta", 0, 0, 1],
  ["Rezygnacja", "W każdej chwili", "W każdej chwili", "W każdej chwili"],
];

function Cell({ v }) {
  if (v === 1) return <Check className="mx-auto h-4 w-4 text-teal-400" />;
  if (v === 0) return <X className="mx-auto h-4 w-4 text-slate-600" />;
  return <span className="text-sm font-medium text-slate-200">{v}</span>;
}

export function PricingCampaign() {
  const choose = (name) => toast.info(`Pakiet ${name} — subskrypcje ruszają wkrótce! Zostaw email w newsletterze, damy znać pierwszym.`);

  return (
    <section id="pakiety" className="relative mx-auto max-w-7xl scroll-mt-24 px-4 py-16 sm:px-6" data-testid="pricing-section">
      <Reveal>
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-teal-500/40 bg-teal-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-teal-300">
            <InfinityIcon className="h-3.5 w-3.5" /> Bez umów rocznych
          </span>
          <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
            Czytaj i słuchaj <span className="gradient-text">bez umów na rok</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-slate-400 sm:text-lg">
            Inni każą Ci się zobowiązać na 12 miesięcy, żeby dostać dobrą cenę. <span className="font-semibold text-slate-200">My nie.</span>
          </p>
        </div>
      </Reveal>

      <div className="mx-auto mt-10 grid max-w-4xl gap-3 sm:grid-cols-2">
        {BENEFITS.map((b, i) => {
          const Icon = b.icon;
          return (
            <Reveal key={i} delay={i * 90}>
              <div className="flex items-start gap-3.5 rounded-2xl glass-panel p-4 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-teal-400/40" data-testid={`benefit-${i}`}>
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 text-slate-950">
                  <Icon className="h-4.5 w-4.5" />
                </div>
                <p className="text-sm leading-relaxed text-slate-200">{b.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>

      <div className="mt-14 grid gap-6 lg:grid-cols-3">
        {PLANS.map((p, i) => (
          <Reveal key={p.key} delay={i * 120}>
            <div className={p.featured ? "glow-border rounded-3xl p-[2px]" : ""} data-testid={`plan-card-${p.key}`}>
              <div className={`relative flex h-full flex-col rounded-3xl p-7 transition-transform duration-300 hover:-translate-y-2 ${p.featured ? "bg-slate-950/95" : "glass-panel"}`}>
                {p.featured && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-cyan-400 to-teal-400 px-4 py-1 text-[11px] font-extrabold uppercase tracking-widest text-slate-950 shadow-lg shadow-cyan-500/30">
                    Najpopularniejszy
                  </span>
                )}
                <h3 className="font-display text-lg font-bold text-slate-100">{p.name}</h3>
                <p className="mt-1 text-xs text-slate-400">{p.desc}</p>
                <div className="mt-5 flex items-baseline gap-1.5">
                  <span className={`font-display text-4xl font-extrabold ${p.featured ? "gradient-text" : "text-white"}`}>{p.price} zł</span>
                  <span className="text-sm text-slate-400">/ miesiąc</span>
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs font-semibold text-cyan-300">
                  <Sparkles className="h-3.5 w-3.5" /> {p.limit}
                </div>
                <button
                  data-testid={`plan-cta-${p.key}`}
                  onClick={() => choose(p.name)}
                  className={`mt-7 w-full rounded-full py-3 font-display text-sm font-bold transition-transform hover:scale-[1.03] active:scale-95 ${
                    p.featured
                      ? "bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950 shadow-lg shadow-cyan-500/30"
                      : "border border-slate-600 text-slate-200 hover:border-cyan-400/60 hover:text-cyan-300"
                  }`}
                >
                  Wybierz {p.name}
                </button>
                <p className="mt-3 text-center text-[11px] text-slate-500">Rezygnacja w każdej chwili</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={150}>
        <div className="mt-12 overflow-x-auto rounded-3xl glass-panel" data-testid="pricing-table">
          <table className="w-full min-w-[640px] text-left">
            <thead>
              <tr className="border-b border-cyan-500/15 text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-4 font-semibold">Pakiety DigiLib — wyłącznie miesięczne</th>
                {PLANS.map((p) => (
                  <th key={p.key} className={`px-4 py-4 text-center font-bold ${p.featured ? "text-cyan-300" : "text-slate-300"}`}>
                    {p.name}
                    <div className="mt-0.5 text-[11px] font-semibold normal-case text-slate-500">{p.price} zł/mies.</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row, i) => (
                <tr key={i} className={`border-b border-slate-800/60 ${i % 2 ? "bg-slate-900/30" : ""}`}>
                  <td className="px-6 py-3.5 text-sm text-slate-300">{row[0]}</td>
                  {row.slice(1).map((v, j) => (
                    <td key={j} className={`px-4 py-3.5 text-center ${PLANS[j].featured ? "bg-cyan-500/5" : ""}`}>
                      <Cell v={v} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Reveal>

      <Reveal delay={200}>
        <p className="mt-8 text-center font-display text-lg font-bold text-slate-200 sm:text-xl">
          Nie musisz się wiązać na rok, <span className="gradient-text">żeby dobrze czytać.</span>
        </p>
      </Reveal>
    </section>
  );
}
